import { ApplicationEntity } from "@graffer-inc/cloud-gov-core";
import {
  runGqlQueryV2,
  gqlClient,
  gqlHeader,
  GetQualifiedInvoiceIssueRequestQuery,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  calcIssueUIState,
  issuedCount,
} from "../../../../../../application/domain/qualified-invoice/issue-request";

type Props = {
  application: ApplicationEntity;
};

export const useQualifiedInvoiceIssueRequest = ({ application }: Props) => {
  const [issueControlStatus, setIssueControlStatus] = useState<{
    isIssuableProcedure: boolean;
    hasTaxablePayment: boolean;
    isPaymentCompleted: boolean;
    isIssuableApplicationStatus: boolean;
  }>({
    isIssuableProcedure: false,
    hasTaxablePayment: false,
    isPaymentCompleted: false,
    isIssuableApplicationStatus: false,
  });
  const [redirectTo, setRedirectTo] = useState<
    "ISSUE_FORM" | "APPLICATION_DETAIL"
  >("APPLICATION_DETAIL");

  const [isLoading, setIsLoading] = useState(false);

  const [issueRequest, setIssueRequest] =
    useState<
      GetQualifiedInvoiceIssueRequestQuery["qualifiedInvoiceIssueRequest"]
    >(undefined);

  const handleOnSetRedirectToIssueForm = useCallback(() => {
    setRedirectTo("ISSUE_FORM");
  }, []);

  const handleOnFetchIssueRequest = async () => {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.getQualifiedInvoiceIssueRequest(
          { applicationId: application.id },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.qualifiedInvoiceIssueControlStatus) {
        setIssueControlStatus(res.data?.qualifiedInvoiceIssueControlStatus);
      }
      if (res.data?.qualifiedInvoiceIssueRequest) {
        setIssueRequest(res.data.qualifiedInvoiceIssueRequest);
      }
    } catch (e) {
      toast.error(
        "支払い明細書の交付の依頼の取得に失敗しました。時間をおいてから、再度お試しください。",
      );
    }
  };

  const handleOnWithdraw = async () => {
    if (window.confirm("交付依頼を取り下げます。\nよろしいですか？")) {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.withdrawQualifiedInvoiceIssueRequest(
            {
              applicationId: application.id,
            },
            gqlHeader("m"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.withdrawQualifiedInvoiceIssueRequest) {
          toast.success("交付依頼を取り下げました。");
          handleOnFetchIssueRequest();
        }
      } catch (e) {
        toast.error(
          "交付依頼の取り下げに失敗しました。時間を置いて再度お試しください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOnRevoke = async () => {
    if (
      window.confirm("交付した支払い明細書を取り消します。\nよろしいですか？")
    ) {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.revokeQualifiedInvoice(
            {
              applicationId: application.id,
            },
            gqlHeader("m"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.revokeQualifiedInvoice) {
          toast.success("支払い明細書を取り消しました。");
          handleOnFetchIssueRequest();
        }
      } catch (e) {
        toast.error(
          "支払い明細書の取り消しに失敗しました。時間を置いて再度お試しください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // fetch again when the application status or application payment is changed
  useEffect(() => {
    handleOnFetchIssueRequest();
  }, [application.status, application.paymentsV2]);

  const issueUIState = calcIssueUIState({
    issueRequest: issueRequest
      ? {
          issuedCount: issuedCount({
            available: issueRequest.availableIssuedQualifiedInvoice,
            revoked: issueRequest.revokedQualifiedInvoices,
          }),
          withdrewAt: issueRequest.withdrewAt,
          hasAvailable: !!issueRequest.availableIssuedQualifiedInvoice,
        }
      : undefined,
    issueControlStatus,
  });

  return {
    redirectTo,
    isLoading,
    issueUIState,
    issueControlStatus,
    availableIssuedQualifiedInvoice:
      issueRequest?.availableIssuedQualifiedInvoice,
    revokedQualifiedInvoices: issueRequest
      ? issueRequest.revokedQualifiedInvoices
      : [],
    handleOnSetRedirectToIssueForm,
    handleOnFetchIssueRequest,
    handleOnWithdraw,
    handleOnRevoke,
  };
};
